import Tooltip from '@components/shared/tooltip/Tooltip';
import { FC, useRef } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { useSearchDeals } from '../../../hooks/header/UseSearchDeals';
import { useCheckDevice } from '../../../hooks/UseCheckDevice';
import useOnScreen from '../../../hooks/useOnScreen';
import { formatDateWithReference } from '../../../services/utils';
import { AppState } from '../../../store/AppState';
import { NotificationResource } from '../../../types';
import { DealerState } from '../../../types/DealerState';
import { IF, StringToJSX } from '../../shared/generics';
import { Icon } from '../../style/Icon';
import { BPS, CarModel, Copy, ListItem } from '../SharedNotificationStyles';
import {
  Actions,
  DealTypeStickerNotification,
  NotifIcon,
  NotifTextContainer,
  ReadControl,
  ReadStatus,
} from './NotificationListItemStyles';

export type NotificationListItemProps = {
  item: NotificationResource;
  isEssentialType: boolean;
  onClick: (item: NotificationResource) => void;
  onRead: (item: NotificationResource) => void;
};

const NotificationListItem: FC<NotificationListItemProps> = ({
  item,
  isEssentialType,
  onClick,
  onRead,
}) => {
  const { dealerInfos } = useSelector<AppState, DealerState>((state) => state.dealer);
  const ref = useRef<HTMLLIElement>();
  const isVisible = useOnScreen(ref);
  const { isDealerOptIn } = useSearchDeals();
  const isOpetIn = dealerInfos.map((e) => isDealerOptIn(e.dealerId));
  const { storeServices, featureToggle } = useSelector<AppState, DealerState>(
    (state) => state.dealer,
  );
  const { isMobileDevice, isTabletDevice } = useCheckDevice();

  const hideIcon = () => {
    return (
      (item.notificationType === 'MdlServiceArrival' && item.notificationValue === 'CDK') ||
      item.notificationType !== 'MdlServiceArrival'
    );
  };

  const handleOnClick = (event: React.MouseEvent<HTMLElement>) => {
    onClick(item);
    event.stopPropagation();
  };

  const toggleRead = (event: React.MouseEvent<HTMLElement>) => {
    onRead(item);
    event.stopPropagation();
  };

  return (
    <ListItem
      isRead={item.isRead}
      ref={ref}
      onClick={handleOnClick}
      data-testid='notification-list-item'
    >
      <IF condition={isVisible}>
        <>
          <NotifIcon data-testid='notif-icon'>
            <Icon additionalClassName='am-icon' type={item.icon} />
          </NotifIcon>
          <NotifTextContainer
            className={item.isTextWithIcon ? 'text-with-icon' : ''}
            data-testid='notif-text-container'
          >
            <Copy isRead={item.isRead} data-testid='copy'>
              <span>
                {' '}
                <StringToJSX domString={item.notificationText} />
              </span>
            </Copy>
            <CarModel isRead={item.isRead} data-testid='car-model'>
              {item.vehicleInfo}
              <IF condition={item.date && !!item.vehicleInfo}>
                <span>.&nbsp;</span>
              </IF>
              <IF condition={item.date && item.notificationType != 'PrivateIncentive'}>
                <b> {formatDateWithReference(item.date)}</b>
              </IF>
            </CarModel>
          </NotifTextContainer>
          <div className='mx-1 pt-3'>
            <IF
              condition={
                item.isPrivateIncentiveApplied &&
                item.notificationType !== 'PrivateIncentive' &&
                item.isConquest &&
                isOpetIn.indexOf(true) > -1
              }
            >
              <Tooltip
                position='bottom'
                content={<span>Private Incentive</span>}
                trigger={<div className='am-icon am-icon-private-incentive float-right small' />}
              />
            </IF>
            <IF
              condition={
                item.isPrivateIncentiveApplied &&
                item.notificationType !== 'PrivateIncentive' &&
                !item.isConquest
              }
            >
              <Tooltip
                position='bottom'
                content={<span>Private Incentive</span>}
                trigger={<div className='am-icon am-icon-private-incentive float-right small' />}
              />
            </IF>
            <IF condition={item.isFmcPrivateOffer && featureToggle?.fordPrivateIncentive}>
              <Tooltip
                position='bottom'
                content={<span>FMC Private Offer</span>}
                trigger={
                  <FordPrivateIncentiveIcon
                    className='am-icon am-icon-incentives-alt float-right small'
                    data-testid='ford-private-incentive-notification'
                  />
                }
              />
            </IF>
            <IF condition={item.isConquest && !isEssentialType}>
              <Tooltip
                position='bottom'
                content={<span>Service-to-Sales</span>}
                trigger={<div className='am-icon am-icon-conquest float-right' />}
              />
            </IF>
            <div
              className={`am-icon ${item.isMdl ? 'am-icon-logo-mdl' : item.isRfid ? 'am-icon-rfid' : item.isConquest ? 'mdl-with-conquest-icon' : hideIcon() ? 'hidden' : ''}`}
            ></div>
            <IF condition={item.isCampaignViewMarketConquestDeal}>
              <div className='am-icon market-conquest1 am-icon-color-grey' />
            </IF>
            <IF condition={item.hasOpenRecall && storeServices.hasRecall && featureToggle.recall}>
              <Tooltip
                position='bottom'
                content={<span>Open Recall</span>}
                trigger={<div className='am-icon-recall float-right' />}
              />
            </IF>
          </div>
          <div className='mx-1 pt-3'>
            <IF condition={item.notificationType != 'PrivateIncentive'}>
              <BPS isRead={item.isRead} data-testid='bps'>
                <em>
                  BPS<span>&reg;</span>
                </em>
                <b className={item.bps === null ? 'b-bps-null' : ''}>
                  {item.bps ? item.bps : '\u2014'}
                </b>
              </BPS>
            </IF>
          </div>
          <Actions data-testid='actions'>
            <IF
              condition={
                item.bps !== null &&
                item.dealType !== null &&
                (item.isCampaignViewMarketConquestDeal || item.isAssignedMarketConquestDeal)
              }
            >
              <DealTypeStickerNotification
                isRead={item.isRead}
                data-testid='deal-type-sticker'
                additionalClassName={'None'}
              />
            </IF>
            <IF
              condition={
                item.bps !== null &&
                item.dealType !== null &&
                !item.isCampaignViewMarketConquestDeal &&
                !item.isAssignedMarketConquestDeal
              }
            >
              <DealTypeStickerNotification
                isRead={item.isRead}
                data-testid='deal-type-sticker'
                additionalClassName={item.dealType}
                title={item.dealType + ' Deal'}
              >
                {item.dealType?.charAt(0).toUpperCase()}
              </DealTypeStickerNotification>
            </IF>
            <IF condition={item.bps === null}>
              <span>&nbsp;</span>
            </IF>
            <Tooltip
              content={
                !(isMobileDevice || isTabletDevice) && (
                  <span>{`Mark as ${item.isRead ? 'Unread' : 'Read'}`}</span>
                )
              }
              position='left'
              trigger={
                <ReadStatus onClick={toggleRead} data-testid='read-status'>
                  <ReadControl isRead={item.isRead} data-testid='read-control' />
                </ReadStatus>
              }
            />
          </Actions>
        </>
      </IF>
    </ListItem>
  );
};

export default NotificationListItem;

const FordPrivateIncentiveIcon = styled.div`
  color: ${({ theme }) => theme.palette.lightGreyEight};
`;
